import {
  Apartment,
  SendOutlined,
  AssignmentOutlined,
  BarChartOutlined,
  HomeOutlined,
  PeopleOutline,
  Security,
  TableChartOutlined,
  HelpOutline,
} from '@mui/icons-material';
import { externalLinks, externalTitles, routes } from '@/constants/Constants';
import { useLocation } from 'react-router';
import { useGoToExternal, useNavigateWithSegment } from '@/utils/linkUtils';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useQuery } from '@tanstack/react-query';
import getUser from '@/utils/apiRestCalls/getUser';
import { shouldDisableRenterInvite, shouldUserStayInEnrollProperty } from '@/utils/commonUtils';
import { LLTypes } from '@/utils/types/User';
import { useUrlChecker } from '@/hooks/useUrlChecker';
import { ZgriVisibilityGuard } from '@/features/RentersInsurance/guards';
import { CoreVisibilityGuard } from '@/components/guards';
import { UserPermissions } from '@/constants/permissions.constant';
import { usePermission } from '@/hooks/usePermission';
import { MenuItem } from './MenuItem';
import { MenuDivider } from './MenuDivider';
import { useFeatureFlag } from '@/hooks';
import { SegmentObj } from '@/utils/types/LinkTypes.interface';

type Props = {
  menuOpen: boolean;
  closeMenu?: () => void;
  persistSectionLabels?: boolean;
};

export const MenuContent = ({ menuOpen, closeMenu, persistSectionLabels }: Props) => {
  const styles = getStyles();
  const navigateWithSegment = useNavigateWithSegment();
  const navigate = (url: string, segmentObj?: SegmentObj) => {
    if (closeMenu) closeMenu();
    navigateWithSegment(url, segmentObj);
  };
  const goToExternal = useGoToExternal();
  const location = useLocation();
  const { isPublic: isPublicRoute } = useUrlChecker();
  const showSectionLabels = persistSectionLabels || menuOpen;

  const isSSUMEnabled = useFeatureFlag('otters-3986_self_service_user_management_enabled');

  const { enabled: canManageUsersEnabled } = usePermission(UserPermissions.User.CanManageUsers);

  const { notSet: canEnrollPropertiesNotSet, enabled: canEnrollPropertiesEnabled } = usePermission(
    UserPermissions.Properties.CanEnrollProperty,
  );
  const { notSet: userCanFileClaimNotSet, enabled: userCanFileClaimEnabled } = usePermission(
    UserPermissions.Claim.CanFileClaim,
  );
  const { notSet: userCanInviteRenterNotSet, enabled: userCanInviteRenterEnabled } = usePermission(
    UserPermissions.Invite.CanInviteRenter,
  );

  const canEnrollProperties = canEnrollPropertiesNotSet || canEnrollPropertiesEnabled;
  const userCanInviteRenter = userCanInviteRenterNotSet || userCanInviteRenterEnabled;
  const userCanFileClaim = userCanFileClaimNotSet || userCanFileClaimEnabled;

  const { notSet: userCanAccessDealsDashboardNotSet, enabled: userCanAccessDealsDashboardEnabled } =
    usePermission(UserPermissions.Deal.CanAccessDealsDashboard);

  const userCanAccessDealsDashboard =
    userCanAccessDealsDashboardNotSet || userCanAccessDealsDashboardEnabled;

  const { enabled: userCanViewReportEnabled } = usePermission(UserPermissions.Claim.CanViewReport);

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: Infinity,
  });

  const goToFAQ = () => {
    if (user && !canEnrollProperties) {
      return goToExternal(externalLinks.operatorsFAQ, {
        title: 'FAQ Menu (Operator)',
        path: externalLinks.operatorsFAQ,
      });
    }

    return goToExternal(externalLinks.faq, {
      title: 'FAQ Menu',
      path: 'enroll-property#faq',
    });
  };

  const shouldDisableActionIcons = shouldUserStayInEnrollProperty(location.pathname, user);
  // Only disable menu items for SMB users without properties - Institutional users manage properties differently
  const noPropertiesEnrolled = user?.LLType === LLTypes.SMB && shouldDisableRenterInvite(user);
  const noPropertiesEnrolledTooltip =
    "This option isn't available until you enroll your first property";

  const userIsOnHome = location.pathname === routes.home;
  const userIsOnUsers = location.pathname.includes(routes.users);
  const userIsOnPropertyEnrollment = location.pathname.includes(routes.propertyEnrollmentForm);
  const userIsOnRenterInvite = location.pathname.includes(routes.renterInvite);
  const userIsOnDealsDashboard = location.pathname.includes(routes.dealsDashboard);
  const userIsOnClaimsReport = location.pathname.includes(routes.claimsReport);
  const userIsOnClaimsForm =
    location.pathname.includes(routes.claimsForm) ||
    location.pathname.includes(routes.claimsFormInstructions) ||
    location.pathname.includes(routes.claimsThankYou);

  return (
    <Grid container sx={styles.container}>
      {!isPublicRoute && (
        <Box>
          <Box sx={styles.sectionContainer}>
            <MenuItem
              open={menuOpen}
              text="Home"
              icon={<HomeOutlined />}
              disabled={noPropertiesEnrolled || (shouldDisableActionIcons && !userIsOnHome)}
              disabledTooltip={noPropertiesEnrolled ? noPropertiesEnrolledTooltip : undefined}
              selected={userIsOnHome}
              action={() => navigate(routes.home, { title: 'Visit Home Menu', path: '/' })}
            />
            {canEnrollProperties && (
              <MenuItem
                open={menuOpen}
                text="Enroll Properties"
                icon={<Apartment />}
                disabled={shouldDisableActionIcons && !userIsOnPropertyEnrollment}
                selected={userIsOnPropertyEnrollment}
                action={() =>
                  navigate(routes.propertyEnrollmentForm, {
                    title: 'Visit Enroll Properties Menu',
                    path: '/enroll/property-type',
                  })
                }
              />
            )}
          </Box>

          <Box sx={styles.sectionContainer}>
            <CoreVisibilityGuard>
              <MenuDivider label="Lease Guarantee" open={showSectionLabels} />

              {user && userCanInviteRenter && (
                <MenuItem
                  icon={<SendOutlined />}
                  open={menuOpen}
                  text="Invite Renters"
                  action={() => navigate(routes.renterInvite)}
                  selected={userIsOnRenterInvite}
                  disabled={
                    (shouldDisableActionIcons && !userIsOnRenterInvite) ||
                    shouldDisableRenterInvite(user)
                  }
                  disabledTooltip={
                    shouldDisableRenterInvite(user) ? noPropertiesEnrolledTooltip : undefined
                  }
                />
              )}

              {user && userCanAccessDealsDashboard && (
                <MenuItem
                  open={menuOpen}
                  text={'Application Tracker'}
                  icon={<TableChartOutlined />}
                  disabled={
                    noPropertiesEnrolled || (shouldDisableActionIcons && !userIsOnDealsDashboard)
                  }
                  disabledTooltip={noPropertiesEnrolled ? noPropertiesEnrolledTooltip : undefined}
                  selected={userIsOnDealsDashboard}
                  action={() => {
                    navigate(routes.dealsDashboard);
                  }}
                />
              )}

              {userCanFileClaim && (
                <MenuItem
                  open={menuOpen}
                  text={'File a Claim'}
                  disabled={noPropertiesEnrolled || shouldDisableActionIcons || !userCanFileClaim}
                  disabledTooltip={noPropertiesEnrolled ? noPropertiesEnrolledTooltip : undefined}
                  icon={<AssignmentOutlined />}
                  action={() => navigate(routes.claimsFormInstructions)}
                  selected={userIsOnClaimsForm}
                />
              )}
            </CoreVisibilityGuard>

            {userCanViewReportEnabled && (
              <MenuItem
                open={menuOpen}
                text="Claims Dashboard"
                disabled={
                  noPropertiesEnrolled || (shouldDisableActionIcons && !userIsOnClaimsReport)
                }
                disabledTooltip={noPropertiesEnrolled ? noPropertiesEnrolledTooltip : undefined}
                icon={<BarChartOutlined />}
                action={() => navigate(routes.claimsReport)}
                selected={userIsOnClaimsReport}
              />
            )}
          </Box>

          <ZgriVisibilityGuard>
            <Box sx={styles.sectionContainer}>
              <MenuDivider label="Renter Insurance" open={showSectionLabels} />

              <MenuItem
                open={menuOpen}
                text="Compliance Dashboard"
                displayText={
                  <Box component={'span'} display={'flex'} alignItems={'center'} columnGap={'8px'}>
                    <Typography
                      sx={{ color: 'inherit', fontSize: '16px !important' }}
                      component={'span'}
                    >
                      Compliance Dashboard
                    </Typography>
                  </Box>
                }
                icon={<Security />}
                disabled={noPropertiesEnrolled}
                disabledTooltip={noPropertiesEnrolled ? noPropertiesEnrolledTooltip : undefined}
                action={() =>
                  navigate(routes.rentersInsurance, {
                    title: 'Visit Renters Insurance',
                    path: '/renters-insurance',
                  })
                }
                selected={location.pathname.includes(routes.rentersInsurance)}
              />
            </Box>
          </ZgriVisibilityGuard>
        </Box>
      )}
      <Box sx={styles.sectionContainer}>
        <MenuDivider />
        {isSSUMEnabled && canManageUsersEnabled && (
          <MenuItem
            open={menuOpen}
            text="User management"
            icon={<PeopleOutline />}
            disabled={noPropertiesEnrolled}
            disabledTooltip={noPropertiesEnrolled ? noPropertiesEnrolledTooltip : undefined}
            action={() => navigate(routes.users)}
            selected={userIsOnUsers}
          />
        )}
        <MenuItem
          open={menuOpen}
          text={externalTitles.faq}
          icon={<HelpOutline />}
          action={goToFAQ}
        />
      </Box>
    </Grid>
  );
};

const getStyles = () => ({
  container: {
    height: '100%',
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    flexDirection: 'column',
  },
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    rowGap: '4px',
    padding: '12px',
    boxSizing: 'border-box',
  },
});
